/* eslint-disable */
import { useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { Form, Input, Select, Button, Card, Space, Typography, Tooltip } from 'antd';
import {
	PhoneOutlined,
	MailOutlined,
	TeamOutlined,
	PlusOutlined,
	DeleteOutlined,
	QuestionCircleOutlined,
	ProfileOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { CRMGroup, CRMLead } from '@/types/TCRM';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Simplified field suggestions for CRM context
const COMMON_FIELD_SUGGESTIONS = [
	{ key: 'name', label: 'Full Name' },
	{ key: 'company', label: 'Company' },
	{ key: 'job_title', label: 'Job Title' },
	{ key: 'location', label: 'Location' },
];

interface CRMLeadCreatorProps {
	phoneNumber?: string;
	initialData?: CRMLead;
	onSubmit: (leadData: Partial<CRMLead>) => void;
	isLoading: boolean;
	groups: CRMGroup[];
}

export interface CRMLeadCreatorRef {
	resetForm: () => void;
}

const CRMLeadCreator = forwardRef<CRMLeadCreatorRef, CRMLeadCreatorProps>(
	({ phoneNumber, initialData, onSubmit, isLoading, groups }, ref) => {
		const [form] = Form.useForm();

		const [customFields, setCustomFields] = useState<
			Array<{ key: string; value: string; isLocked: boolean }>
		>([]);

		useEffect(() => {
			if (initialData?.dynamic_fields) {
				setCustomFields(
					Object.entries(initialData.dynamic_fields).map(([key, value]) => ({
						key,
						value: String(value),
						isLocked: true,
					})),
				);
			} else {
				setCustomFields([]);
			}
		}, [initialData]);

		useEffect(() => {
			if (initialData) {
				form.setFieldsValue({
					phoneNumber: initialData?.phone_number,
					email: initialData?.email,
					groupId: initialData.group_id,
				});
			} else if (phoneNumber) {
				form.setFieldsValue({ phoneNumber });
			}
		}, [initialData, phoneNumber, form]);

		useImperativeHandle(ref, () => ({
			resetForm: () => {
				form.resetFields();
				setCustomFields([]);
			},
		}));

		const handleSubmit = () => {
			form.validateFields().then((values) => {
				// Collect all custom field values
				const dynamicFields = customFields.reduce((acc: { [key: string]: string }, field) => {
					if (field.key) {
						acc[field.key] = field.value || '';
					}
					return acc;
				}, {});

				const leadData: Partial<CRMLead> = {
					...(initialData && { ...initialData }),
					phone_number: values.phoneNumber,
					email: values.email,
					group_id: values.groupId,
					dynamic_fields: dynamicFields,
				};

				onSubmit(leadData);
			});
		};

		const addCommonField = (fieldKey: string) => {
			// Check if field already exists
			const exists = customFields.some((field) => field.key === fieldKey);
			if (!exists) {
				setCustomFields([...customFields, { key: fieldKey, value: '', isLocked: true }]);
			}
		};

		return (
			<Card className="shadow-md rounded-lg overflow-hidden border-0">
				<div className="flex justify-between items-center border-b pb-4 mb-6">
					<Title level={4} className="m-0 text-gray-800 flex items-center">
						{initialData ? 'Edit Lead' : 'New Lead'}
						<Tooltip title="Enter contact information and any additional details you want to track for this lead">
							<QuestionCircleOutlined className="ml-2 text-gray-400 text-base" />
						</Tooltip>
					</Title>
				</div>

				<Form form={form} layout="vertical">
					<div className="mb-6">
						<div className="flex items-center mb-3">
							<ProfileOutlined className="mr-2 text-blue-500" />
							<Text strong className="text-lg">
								Contact Details
							</Text>
						</div>

						<Form.Item
							name="phoneNumber"
							label={
								<span className="flex items-center">
									Phone Number
									<Tooltip title="The primary contact number for this lead">
										<InfoCircleOutlined className="ml-1 text-gray-400" />
									</Tooltip>
								</span>
							}
							rules={[
								{ required: true, message: 'Phone number is required' },
								{
									pattern: /^\+?[\d\s-]+$/,
									message: 'Please enter a valid phone number',
								},
							]}
						>
							<Input
								prefix={<PhoneOutlined className="text-gray-400" />}
								placeholder="+1 234 567 8900"
								size="large"
							/>
						</Form.Item>

						<Form.Item
							name="email"
							label={
								<span className="flex items-center">
									Email Address
									<Tooltip title="The email address where you can reach this lead">
										<InfoCircleOutlined className="ml-1 text-gray-400" />
									</Tooltip>
								</span>
							}
							rules={[{ type: 'email', message: 'Please enter a valid email' }]}
						>
							<Input
								prefix={<MailOutlined className="text-gray-400" />}
								placeholder="customer@example.com"
								size="large"
							/>
						</Form.Item>

						<Form.Item
							name="groupId"
							label={
								<span className="flex items-center">
									Lead Group
									<Tooltip title="Categories help you organize your leads">
										<InfoCircleOutlined className="ml-1 text-gray-400" />
									</Tooltip>
								</span>
							}
							rules={[{ required: true, message: 'Please select a group' }]}
						>
							<Select
								placeholder="Select a lead group"
								size="large"
								suffixIcon={<TeamOutlined className="text-gray-400" />}
							>
								{groups?.map((group) => (
									<Option key={group.group_id} value={group.group_id}>
										{group.group_name}
									</Option>
								))}
							</Select>
						</Form.Item>
					</div>

					{/* Custom Fields Section */}
					<div className="mb-6">
						<div className="flex items-center justify-between mb-3">
							<div className="flex items-center">
								<ProfileOutlined className="mr-2 text-blue-500" />
								<Text strong className="text-lg">
									Custom Fields
								</Text>
								<Tooltip title="Add any information you want to track about this lead">
									<InfoCircleOutlined className="ml-2 text-gray-400" />
								</Tooltip>
							</div>
							<Tooltip title="Add a new custom field">
								<Button
									type="primary"
									icon={<PlusOutlined />}
									onClick={() =>
										setCustomFields([...customFields, { key: '', value: '', isLocked: false }])
									}
									size="middle"
									ghost
								>
									Add Field
								</Button>
							</Tooltip>
						</div>

						{customFields.length === 0 ? (
							<div className="text-center p-6 bg-gray-50 rounded-lg border border-dashed">
								<Paragraph>
									No custom fields yet. Add information you want to track about this lead.
								</Paragraph>
								<div className="mt-3">
									{COMMON_FIELD_SUGGESTIONS.map((suggestion) => (
										<Button
											key={suggestion.key}
											type="default"
											className="m-1"
											onClick={() => addCommonField(suggestion.key)}
										>
											+ {suggestion.label}
										</Button>
									))}
								</div>
							</div>
						) : (
							<>
								{customFields.map((field, index) => (
									<div key={index} className="flex gap-2 mb-4">
										<Tooltip
											title={
												field.isLocked ? 'This field name cannot be edited' : 'Enter field name'
											}
										>
											<Input
												value={field.key}
												onChange={(e) => {
													if (!field.isLocked) {
														const newFields = [...customFields];
														newFields[index].key = e.target.value;
														setCustomFields(newFields);
													}
												}}
												placeholder="Field name"
												className="w-2/5"
												size="large"
												disabled={field.isLocked}
											/>
										</Tooltip>
										<Tooltip title="Enter the value for this field">
											<Input
												value={field.value}
												onChange={(e) => {
													const newFields = [...customFields];
													newFields[index].value = e.target.value;
													setCustomFields(newFields);
												}}
												placeholder="Value"
												className="flex-1"
												size="large"
											/>
										</Tooltip>
										<Tooltip title="Remove this field">
											<Button
												type="text"
												danger
												icon={<DeleteOutlined />}
												onClick={() => {
													const newFields = customFields.filter((_, i) => i !== index);
													setCustomFields(newFields);
												}}
												className="flex-shrink-0"
											/>
										</Tooltip>
									</div>
								))}

								<div className="mt-3">
									<Text type="secondary">Add suggested field:</Text>
									<div className="flex flex-wrap gap-2 mt-1">
										{COMMON_FIELD_SUGGESTIONS.filter(
											(suggestion) => !customFields.some((field) => field.key === suggestion.key),
										).map((suggestion) => (
											<Button
												key={suggestion.key}
												type="default"
												size="small"
												onClick={() => addCommonField(suggestion.key)}
											>
												+ {suggestion.label}
											</Button>
										))}
									</div>
								</div>
							</>
						)}
					</div>
				</Form>

				<div className="flex justify-end mt-6 pt-4 border-t">
					<Space>
						<Button onClick={() => form.resetFields()}>Cancel</Button>
						<Button type="primary" onClick={handleSubmit} loading={isLoading}>
							{initialData ? 'Save Changes' : 'Create Lead'}
						</Button>
					</Space>
				</div>
			</Card>
		);
	},
);

export default CRMLeadCreator;
